import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { TextAreaAutoSize } from "@/Common/Components/Inputs/TextAreaAutoSize";
import { LISRISCode } from "@/Common/Models/LISRISCode";
import { LabRequestBillCode } from "@/Common/Models/Patient/LabRequest";
import { DirectorySelectableEntity } from "@/ContactDirectory/Models/DirectorySelectableEntity";
import { IDirectoryEntity } from "@/ContactDirectory/Models/IDirectoryEntity";
import { ConsultationRequest } from "@/Portfolio/Models/ConsultationRequest";
import { IMedicalRequest } from "@/Portfolio/Models/IMedicalRequest";
import { LabRequest } from "@/Portfolio/Models/LabRequest";
import { MedicalRequestFormRecipientField } from "./MedicalRequestFormRecipientField";
import { MedicalRequestFormRowComponent } from "./MedicalRequestFormRow";

import stylesImport from "./MedicalRequestPopupController.module.scss";
import legacyStylesImport from "./MedicalRequestPopupControllerLegacy.module.scss";

interface IMedicalRequestViewComponentProps {
    model: IMedicalRequest;
    disableDescriptionMaxHeight?: boolean;
    disabled?: boolean;
    onRecipientChange?: (recipient: string) => void;
    onRecipientSearchTextChange?: (searchText: string) => void;
    onContactDirectoryEntityChanged?: (selection: DirectorySelectableEntity<IDirectoryEntity>) => void;
    onClearRecipient?: () => void;
    attachDocumentComponent: JSX.Element;
    editContentWarningMessageLabel?: string | null;
}

class MedicalRequestViewComponentState {
    public allLisRisCode: LISRISCode[] = [];
}

class MedicalRequestViewComponent extends BaseReactComponent<IMedicalRequestViewComponentProps, MedicalRequestViewComponentState> {
    private readonly styles = cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements
        ? stylesImport
        : legacyStylesImport;

    constructor(props: IMedicalRequestViewComponentProps) {
        super(props);
        this.state = new MedicalRequestViewComponentState();
    }

    public async componentDidMount(): Promise<void> {
        if (cacheStore.IsChristus()) {
            const codes = await LISRISCode.GetAll();
            this.mutateState(s => s.allLisRisCode = codes);
        }
    }

    public render(): JSX.Element {
        const model = this.props.model;
        if (!model)
            return null;

        const staff = cacheStore.GetCliniciansPersonsByContactFacetId(model.requestingStaffContactFacetId);
        const priority = model.getPriority();

        const labRequest = model as LabRequest;
        const consultationModel = model as ConsultationRequest;
        const descriptionClassName = this.props.disableDescriptionMaxHeight
            ? `${this.styles.descriptionRow}`
            : `${this.styles.descriptionRow} ${this.styles.staticHeight}`;
        const priorityClassName = priority && model.isUrgent()
            ? `${this.styles.priorityRow} ${this.styles.isUrgent}`
            : this.styles.priorityRow;
        const hideBillCodesRow = !(labRequest && labRequest.billCodes && labRequest.billCodes.length > 0);

        return (
            <>
                <MedicalRequestFormRowComponent label="RequestingStaffLabel" dataCy="RequestingStaffRow" className={this.styles.requestStaffRow}>
                    {staff?.NameWithPrefix}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="PriorityLabel" dataCy="PriorityRow" className={priorityClassName}>
                    {priority?.getName()}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="SpecialityLabel" dataCy="SpecialtyRow" className={this.styles.specialtyRow} shouldHide={!this.isConsultationRequest}>
                    {consultationModel.speciality}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="RecipientLabel" dataCy="RecipientRow" className={this.styles.recipientRow} shouldHide={!this.isConsultationRequest || cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements}>
                    {this.renderRecipient(model)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="ReasonLabel" dataCy="ReasonRow" className={this.styles.reasonRow}>
                    {model.reason}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="DescriptionLabel" dataCy="DescriptionRow" className={descriptionClassName}>
                    <TextAreaAutoSize
                        id="description_requestView"
                        cssClass={this.styles.descriptionContent}
                        value={model.description}
                        maxHeight="150px"
                        disabled
                    />
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="AttachmentsLabel" dataCy="AttachmentsRow" className={this.styles.attachmentsRow} shouldHide={this.shouldHideAttachmentsRow}>
                    {this.props.attachDocumentComponent}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent dataCy="WarningMessageRow" className={this.styles.warningMessageRow} shouldHide={!(cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements && this.props.editContentWarningMessageLabel)}>
                    {cacheStore.GetString(this.props.editContentWarningMessageLabel)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="BillingCodeLabel" dataCy="BillCodesRow" className={this.styles.billingCodeRow} shouldHide={hideBillCodesRow}>
                    {this.renderSelectedCodes(labRequest.billCodes)}
                </MedicalRequestFormRowComponent>
            </>
        );
    }

    private renderSelectedCodes(billCodes: LabRequestBillCode[]): JSX.Element[] {
        if (!this.state?.allLisRisCode || !billCodes?.any())
            return [];

        const selectedCodes = billCodes
            .map(code => {
                let lisRisCode = this.state.allLisRisCode.firstOrDefault(bill =>
                    bill.Code === code.Number
                    && bill.CostCenterId == code.CostCenterId
                );
                if (lisRisCode)
                    return lisRisCode;

                let emptyCode = new LISRISCode();
                emptyCode.Code = code.Number;
                emptyCode.Description = "";
                emptyCode.Price = "0";
                return emptyCode;
            });

        return selectedCodes.map((c, idx) => {
            const price: number = +c.Price;
            return (
                <div
                    className="flex-container"
                    key={"code" + idx}
                    style={{ justifyContent: "flex-start" }}
                >
                    <div>
                        <span style={{ color: "#0070d2" }}>{`${c.Description}  (${c.Code}) ${price.toFixed(2)} $ `}</span>
                    </div>
                </div>
            );
        });
    }

    private renderLockedRecipient(model: IMedicalRequest): string {
        return (model as ConsultationRequest).recipient;
    }

    private renderRecipient(model: IMedicalRequest): JSX.Element | string {
        // Christus has its own billing system, changing the recipient may result into inconsistency.
        if (cacheStore.IsChristus())
            return this.renderLockedRecipient(model);

        return (
            <MedicalRequestFormRecipientField
                model={this.props.model}
                disabled={this.props.disabled}
                onRecipientChange={(selection: string) => this.props.onRecipientChange?.(selection)}
                onRecipientSearchTextChange={(textValue: string) => this.props.onRecipientSearchTextChange?.(textValue)}
                onContactDirectoryEntityChanged={this.props.onContactDirectoryEntityChanged}
                onClearRecipient={this.props.onClearRecipient}
            />
        );
    }

    private get isConsultationRequest(): boolean {
        return this.props.model instanceof ConsultationRequest;
    }

    private get shouldHideAttachmentsRow(): boolean {
        if (!this.props.attachDocumentComponent)
            return true;

        if (
            (
                cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements
                || cacheStore.ClinicSettings.EnableDefaultAttachmentsOnConsultationRequests
            )
            && this.isConsultationRequest
        ) {
            return false;
        }

        return !cacheStore.ClinicSettings.EnableConsultationExamRequestAttachments;
    }
}

export { IMedicalRequestViewComponentProps, MedicalRequestViewComponent };
